import React from 'react';
import { Message, ChatConfig, MattressProduct } from '../types';
import ChatMessage from './ChatMessage';
import { HelperButtons } from './HelperButtons';

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  config: ChatConfig | null;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  handleProductSelect: (product: MattressProduct) => void;
  endSession: (reason?: string) => void;
  handleSendMessage?: (content: string) => Promise<void>;
}

/**
 * Renders the list of chat messages, loading indicator, and error messages.
 */
export const MessageList: React.FC<MessageListProps> = ({
  messages,
  isLoading,
  error,
  config,
  messagesEndRef,
  handleProductSelect,
  endSession,
  handleSendMessage,
}) => {
  // Check if only the initial greeting is present
  const showHelperButtons = messages.length === 1 && 
    messages[0].role === 'assistant' && 
    messages[0].id === 'initial-greeting';

  // Handle helper button click
  const handleHelperButtonClick = async (option: string) => {
    if (handleSendMessage) {
      await handleSendMessage(option);
    }
  };

  return (
    <div className="messages-container h-full overflow-y-auto py-6 px-3 md:px-5 space-y-5">
      {messages.map((msg) => (
        <ChatMessage 
          key={msg.id} 
          message={msg} 
          config={config} 
          onProductSelect={handleProductSelect}
        />
      ))}
      
      {/* Helper buttons after initial greeting */}
      {showHelperButtons && handleSendMessage && (
        <HelperButtons 
          config={config} 
          onButtonClick={handleHelperButtonClick}
        />
      )}
      
      {/* Loading indicator */}
      {isLoading && (
        <div className="flex justify-start mb-4">
          <div className="flex items-end">
            <div className="bg-gray-200 rounded-lg p-4 max-w-xs sm:max-w-md relative ml-2 shadow-sm">
              <div className="flex space-x-2">
                <div className="w-3 h-3 rounded-full bg-gray-400 animate-bounce"></div>
                <div className="w-3 h-3 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-3 h-3 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Error display */}
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 mx-2 rounded-r-lg shadow-sm">
          <p className="font-bold text-base">Error</p>
          <p className="text-base leading-relaxed">{error}</p>
          <button 
            onClick={() => {
              endSession('error_reset'); // Add a reason for session end
              window.location.reload(); // Reload page after ending session
            }}
            className="mt-2 text-sm text-red-800 underline"
          >
            Reset Chat
          </button>
        </div>
      )}
      
      <div ref={messagesEndRef} className="h-4" />
    </div>
  );
}; 